<script setup lang="ts">
import useBreakpointsLimit from "~/composables/use-breakpoints-limit";
import { SEGMENT_TRACK_EVENTS } from "~/constants/segment";
import useEventStore from "~/stores/event";
import useUserStore from "~/stores/user";

const { t } = useI18n();
const route = useRoute();
const segment = useSegment();
const userStore = useUserStore();
const eventStore = useEventStore();
const appConfig = useRuntimeConfig().public;
const eventId = route.params.eventId as string;
const isLoggedIn = computed(() => Boolean(userStore.user));
const userVideos = computed(() => eventStore.userClips.concat(eventStore.userHighlights));
const gameHighlights = computed(() => eventStore.gameHighlights);
const playerHighlights = computed(() => eventStore.playerHighlights);
const hasContent = computed(() => userVideos.value.length || eventStore.gameHighlights.length || eventStore.playerHighlights.length);
const isGameHighlightsEnabled = appConfig.APP_CUSTOMER.enable_game_highlights;
const isPlayerHighlightsEnabled = appConfig.APP_CUSTOMER.enable_player_highlights;
const hasPlayerError = computed(() => eventStore.playerState?.error !== null);
const isEventStartingSoon = computed(() => eventStore.isEventStartingSoon);
const isPlaying = computed(() => eventStore.playerState?.playing);
const isClippingModeEnabled = appConfig.APP_CUSTOMER.web_sdk.enable_clipping;
const playerHighlightsLimit = useBreakpointsLimit({
  sm: 3,
  md: 3,
  lg: 5,
  xl: 4,
  xl2: 6,
  xl3: 6,
  xl4: 6,
  xl5: 6,
  xl6: 6,
});

const createClip = () => {
  eventStore.clips.isCreatingClip = true;
  eventStore.tags.isCreatingTag = false;
  segment.track(SEGMENT_TRACK_EVENTS.CLIPPING_PROCESS_STARTED, { event_id: eventId });
};
</script>

<template>
  <template v-if="hasContent">
    <div class="h-full max-h-[49vh]">
      <ListVideos
        v-if="isGameHighlightsEnabled"
        :title="t('labels.game_highlights')"
        :items="gameHighlights"
        type="highlight"
        :limit="2"
        has-show-more-button
        :loading="eventStore.loading"
        :is-playing="isPlaying"
        hide-on-no-content
      />

      <ListVideos
        v-if="isPlayerHighlightsEnabled"
        :title="t('labels.player_highlights')"
        :items="playerHighlights"
        type="player"
        has-show-more-button
        :limit="playerHighlightsLimit"
        :loading="eventStore.loading"
        :is-playing="isPlaying"
        hide-on-no-content
      />

      <ListVideos
        :title="t('labels.your_clips')"
        :items="userVideos"
        type="clip"
        :loading="eventStore.loading"
        :is-playing="isPlaying"
        hide-on-no-content
      />
    </div>
  </template>
  <template v-else>
    <div class="flex flex-col items-center w-full px-4 py-10 text-center">
      <IconClip fill="var(--base-icon-color)" :width="32" :height="32" />
      <template v-if="!isClippingModeEnabled">
        <p class="py-6 text-body-medium text-secondary">
          {{ t("pages.events.tabs.event_videos.title_enabled_clipping") }}
        </p>
      </template>
      <template v-else-if="isLoggedIn">
        <p class="py-6 text-body-medium text-secondary">
          {{ t("pages.events.tabs.event_videos.title_no_clips") }}
        </p>
        <BaseButton name="createTag" class="mb-5" :disabled="hasPlayerError || isEventStartingSoon" type="primary" @click="createClip">
          <div class="flex items-center gap-2">
            <IconClip :width="16" :height="16" fill="#ffffff" />
            <span class="text-button-bold">{{ t("pages.events.tabs.event_videos.create_clip") }}</span>
          </div>
        </BaseButton>
      </template>
      <template v-else>
        <p class="py-6 text-body-medium text-secondary">
          {{ t("pages.events.tabs.event_videos.title_no_clips_not_logged_in") }}
        </p>
        <AuthButton
          mode="sign-in"
          :title="t('labels.sign_in')"
          class="font-bold text-white"
          :redirect-url="route.fullPath"
        />
      </template>
    </div>
  </template>
</template>
